/**
 * useCampanaForm — Estado, validación, guardado y autosave del composer de
 * campañas (§8.3.2).
 *
 * Flujo de guardado con imagen local (blob):
 *   sin campanaId → crear sin imagen → subir blob a Storage → actualizar con
 *   imagenUrl + imagenStoragePath. Con campanaId → subir primero y guardar todo.
 *
 * Autosave: cada 30 s si hay cambios Y ya existe campanaId (silencioso).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  actualizarCampanaFn,
  crearCampanaFn,
  enviarCampanaFn,
  subirImagenCampana,
} from './notificacionesApi';
import {
  CATALOGO_BOTONES,
  esTelefono,
  esUrlHttps,
  slugify,
  type BotonCampana,
  type BotonId,
  type Campana,
  type CampanaPayload,
  type EnviarCampanaResult,
  type EstadoCampana,
  type ImagenCampanaState,
  type RecurrenciaPayload,
  type RecurrenciaTipo,
  type TtlHoras,
} from './tiposCampana';

// ─── Estado del formulario ───────────────────────────────────

export type ModoProgramacion = 'ahora' | 'programada' | 'recurrente';
export type FinRecurrencia = 'nunca' | 'fecha' | 'envios';

export interface BotonSeleccionado {
  id: BotonId;
  valor: string;
}

export interface CampanaFormState {
  nombre: string;
  titulo: string;
  descripcion: string;
  imagen: ImagenCampanaState | null;
  linkMasInfo: string;
  utmActivo: boolean;
  utmParams: string;
  botones: BotonSeleccionado[];
  programacion: ModoProgramacion;
  programadaPara: string; // valor de input datetime-local
  recTipo: RecurrenciaTipo;
  recDiaSemana: number;
  recDiaMes: number;
  recHora: string; // 'HH:mm'
  recFin: FinRecurrencia;
  recFinFecha: string; // valor de input date
  recMaxEnvios: string;
  ttlHoras: TtlHoras;
  prioridad: 'normal' | 'alta';
}

export type ErroresForm = Partial<Record<string, string>>;

const FORM_INICIAL: CampanaFormState = {
  nombre: '',
  titulo: '',
  descripcion: '',
  imagen: null,
  linkMasInfo: '',
  utmActivo: false,
  utmParams: '',
  botones: [],
  programacion: 'ahora',
  programadaPara: '',
  recTipo: 'semanal',
  recDiaSemana: 1,
  recDiaMes: 1,
  recHora: '09:00',
  recFin: 'nunca',
  recFinFecha: '',
  recMaxEnvios: '10',
  ttlHoras: 72,
  prioridad: 'normal',
};

// ─── Helpers de fechas para inputs ───────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Date → valor de input datetime-local (zona local del navegador). */
export function toDatetimeLocal(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function toDateInput(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// ─── UTM ─────────────────────────────────────────────────────

export function utmParamsDefault(nombre: string, campanaId: string | null): string {
  const campaign = campanaId ?? slugify(nombre || 'campana');
  return `utm_source=push&utm_medium=notificacion&utm_campaign=${campaign}`;
}

/** Compone el link final que se guarda (base + UTM si está activo). */
export function linkFinal(form: CampanaFormState): string | null {
  const base = form.linkMasInfo.trim();
  if (!base) return null;
  if (!form.utmActivo || !form.utmParams.trim()) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}${form.utmParams.trim()}`;
}

/** Separa un link guardado en base + parámetros UTM (para modo edición). */
function separarUtm(link: string): { base: string; utm: string | null } {
  try {
    const url = new URL(link);
    const utmEntries: string[] = [];
    const restantes = new URLSearchParams();
    url.searchParams.forEach((valor, clave) => {
      if (clave.startsWith('utm_')) utmEntries.push(`${clave}=${valor}`);
      else restantes.append(clave, valor);
    });
    if (utmEntries.length === 0) return { base: link, utm: null };
    const query = restantes.toString();
    const base = `${url.origin}${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
    return { base, utm: utmEntries.join('&') };
  } catch {
    return { base: link, utm: null };
  }
}

// ─── Botones ─────────────────────────────────────────────────

export function botonesPayload(form: CampanaFormState): BotonCampana[] | null {
  if (form.botones.length === 0) return null;
  return form.botones.map((b) => {
    const item = CATALOGO_BOTONES.find((c) => c.id === b.id);
    const tipo = item?.tipo ?? 'url';
    let valor = b.valor.trim();
    if (b.id === 'llamar' && !valor.startsWith('tel:')) {
      valor = `tel:${valor.replace(/[\s\-()]/g, '')}`;
    }
    return { id: b.id, tipo, valor };
  });
}

// ─── Validación ──────────────────────────────────────────────

export function validarForm(form: CampanaFormState): ErroresForm {
  const errores: ErroresForm = {};

  if (!form.nombre.trim()) errores.nombre = 'Ingrese el nombre interno de la campaña.';
  if (!form.titulo.trim()) errores.titulo = 'El título es obligatorio.';
  else if (form.titulo.length > 100) errores.titulo = 'Máximo 100 caracteres.';
  if (!form.descripcion.trim()) errores.descripcion = 'La descripción es obligatoria.';
  else if (form.descripcion.length > 500) errores.descripcion = 'Máximo 500 caracteres.';

  const link = form.linkMasInfo.trim();
  if (link && !esUrlHttps(link)) {
    errores.linkMasInfo = 'Debe ser una URL válida que empiece con https://';
  }

  for (const b of form.botones) {
    const valor = b.valor.trim();
    if (b.id === 'mas_info') {
      if (!esUrlHttps(valor)) errores[`boton_${b.id}`] = 'Ingrese una URL https válida.';
    } else if (b.id === 'llamar') {
      if (!esTelefono(valor.replace(/^tel:/, ''))) {
        errores[`boton_${b.id}`] = 'Ingrese un número de teléfono válido.';
      }
    } else if (!valor) {
      errores[`boton_${b.id}`] = 'Ingrese el deep link de la acción.';
    }
  }

  if (form.programacion === 'programada') {
    if (!form.programadaPara) {
      errores.programadaPara = 'Seleccione la fecha y hora de envío.';
    } else {
      const ms = new Date(form.programadaPara).getTime();
      if (Number.isNaN(ms)) errores.programadaPara = 'Fecha inválida.';
      else if (ms < Date.now() + 5 * 60_000) {
        errores.programadaPara = 'Debe ser al menos 5 minutos en el futuro.';
      } else if (ms > Date.now() + 30 * 24 * 3_600_000) {
        errores.programadaPara = 'Máximo 30 días en el futuro.';
      }
    }
  }

  if (form.programacion === 'recurrente') {
    if (!form.recHora) errores.recHora = 'Seleccione la hora de envío.';
    if (form.recTipo === 'mensual' && (form.recDiaMes < 1 || form.recDiaMes > 28)) {
      errores.recDiaMes = 'El día del mes debe estar entre 1 y 28.';
    }
    if (form.recFin === 'fecha' && !form.recFinFecha) {
      errores.recFinFecha = 'Seleccione la fecha de finalización.';
    }
    if (form.recFin === 'envios') {
      const n = Number(form.recMaxEnvios);
      if (!Number.isInteger(n) || n < 1) {
        errores.recMaxEnvios = 'Ingrese un número de envíos válido (mínimo 1).';
      }
    }
  }

  return errores;
}

// ─── Payload ─────────────────────────────────────────────────

function recurrenciaPayload(form: CampanaFormState): RecurrenciaPayload {
  const rec: RecurrenciaPayload = {
    tipo: form.recTipo,
    hora: form.recHora,
    finalizaEl: null,
    maxEnvios: null,
  };
  if (form.recTipo === 'semanal') rec.diaSemana = form.recDiaSemana;
  if (form.recTipo === 'mensual') rec.diaMes = form.recDiaMes;
  if (form.recFin === 'fecha' && form.recFinFecha) {
    rec.finalizaEl = new Date(`${form.recFinFecha}T23:59:59`).getTime();
  }
  if (form.recFin === 'envios') {
    rec.maxEnvios = Number(form.recMaxEnvios) || null;
  }
  return rec;
}

interface ImagenResuelta {
  imagenUrl: string | null;
  imagenStoragePath: string | null;
}

function buildPayload(
  form: CampanaFormState,
  imagen: ImagenResuelta,
  incluirProgramacion: boolean,
): CampanaPayload {
  return {
    nombre: form.nombre.trim(),
    titulo: form.titulo.trim(),
    descripcion: form.descripcion.trim(),
    imagenUrl: imagen.imagenUrl,
    imagenStoragePath: imagen.imagenStoragePath,
    linkMasInfo: linkFinal(form),
    botones: botonesPayload(form),
    programadaPara:
      incluirProgramacion && form.programacion === 'programada' && form.programadaPara
        ? new Date(form.programadaPara).getTime()
        : null,
    recurrencia:
      incluirProgramacion && form.programacion === 'recurrente'
        ? recurrenciaPayload(form)
        : null,
    ttlHoras: form.ttlHoras,
    prioridad: form.prioridad,
  };
}

/** Imagen ya resuelta (sin blob pendiente) según el estado del form. */
function imagenSinBlob(imagen: ImagenCampanaState | null): ImagenResuelta {
  if (!imagen || imagen.tipo === 'blob') return { imagenUrl: null, imagenStoragePath: null };
  if (imagen.tipo === 'url') return { imagenUrl: imagen.url, imagenStoragePath: null };
  return { imagenUrl: imagen.url, imagenStoragePath: imagen.storagePath };
}

// ─── Hook principal ──────────────────────────────────────────

export type ModoGuardado = 'borrador' | 'programar' | 'recurrencia';

interface UseCampanaFormResult {
  form: CampanaFormState;
  setField: <K extends keyof CampanaFormState>(campo: K, valor: CampanaFormState[K]) => void;
  errores: ErroresForm;
  setErrores: (e: ErroresForm) => void;
  /** true mientras carga el doc en modo edición. */
  cargando: boolean;
  campanaId: string | null;
  modoEdicion: boolean;
  estadoOriginal: EstadoCampana | null;
  /** null = editable; string = motivo por el que no se puede editar. */
  noEditableMotivo: string | null;
  guardando: boolean;
  enviando: boolean;
  /** Epoch ms del último guardado exitoso (manual o autosave). */
  ultimoGuardado: number | null;
  hayCambios: boolean;
  /** Valida y guarda. Devuelve el campanaId o null si la validación falló. */
  guardar: (modo: ModoGuardado) => Promise<string | null>;
  /** Guarda como borrador y dispara el envío inmediato. */
  enviarAhora: () => Promise<{ campanaId: string; resultado: EnviarCampanaResult } | null>;
}

export function useCampanaForm(campanaIdParam: string | undefined): UseCampanaFormResult {
  const [form, setForm] = useState<CampanaFormState>(FORM_INICIAL);
  const [errores, setErrores] = useState<ErroresForm>({});
  const [cargando, setCargando] = useState(!!campanaIdParam);
  const [campanaId, setCampanaId] = useState<string | null>(campanaIdParam ?? null);
  const [estadoOriginal, setEstadoOriginal] = useState<EstadoCampana | null>(null);
  const [noEditableMotivo, setNoEditableMotivo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [ultimoGuardado, setUltimoGuardado] = useState<number | null>(null);
  const [hayCambios, setHayCambios] = useState(false);

  const modoEdicion = !!campanaIdParam;

  // Refs para autosave (evita re-crear el intervalo con cada cambio)
  const formRef = useRef(form);
  formRef.current = form;
  const campanaIdRef = useRef(campanaId);
  campanaIdRef.current = campanaId;
  const hayCambiosRef = useRef(hayCambios);
  hayCambiosRef.current = hayCambios;
  const guardandoRef = useRef(guardando);
  guardandoRef.current = guardando;

  const setField = useCallback(
    <K extends keyof CampanaFormState>(campo: K, valor: CampanaFormState[K]) => {
      setForm((prev) => ({ ...prev, [campo]: valor }));
      setHayCambios(true);
      setErrores((prev) => {
        if (!prev[campo as string]) return prev;
        const next = { ...prev };
        delete next[campo as string];
        return next;
      });
    },
    [],
  );

  // ─── Carga en modo edición ─────────────────────────────────
  useEffect(() => {
    if (!campanaIdParam) return;
    let activo = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'campanas', campanaIdParam));
        if (!activo) return;
        if (!snap.exists()) {
          setNoEditableMotivo('La campaña no existe o fue eliminada.');
          return;
        }
        const data = snap.data() as Omit<Campana, 'id'>;
        if (!['borrador', 'programada', 'recurrente'].includes(data.estado)) {
          setNoEditableMotivo(
            'Solo se pueden editar campañas en borrador, programadas o recurrentes.',
          );
          return;
        }
        setEstadoOriginal(data.estado);

        const { base, utm } = data.linkMasInfo
          ? separarUtm(data.linkMasInfo)
          : { base: '', utm: null };

        const rec = data.recurrencia;
        setForm({
          nombre: data.nombre ?? '',
          titulo: data.titulo ?? '',
          descripcion: data.descripcion ?? '',
          imagen: data.imagenUrl
            ? data.imagenStoragePath
              ? { tipo: 'existente', url: data.imagenUrl, storagePath: data.imagenStoragePath }
              : { tipo: 'url', url: data.imagenUrl }
            : null,
          linkMasInfo: base,
          utmActivo: !!utm,
          utmParams: utm ?? '',
          botones: (data.botones ?? []).map((b) => ({
            id: b.id,
            valor: b.id === 'llamar' ? b.valor.replace(/^tel:/, '') : b.valor,
          })),
          programacion: rec ? 'recurrente' : data.programadaPara ? 'programada' : 'ahora',
          programadaPara: data.programadaPara
            ? toDatetimeLocal(data.programadaPara.toDate())
            : '',
          recTipo: rec?.tipo ?? 'semanal',
          recDiaSemana: rec?.diaSemana ?? 1,
          recDiaMes: rec?.diaMes ?? 1,
          recHora: rec?.hora ?? '09:00',
          recFin: rec?.finalizaEl ? 'fecha' : rec?.maxEnvios ? 'envios' : 'nunca',
          recFinFecha: rec?.finalizaEl ? toDateInput(rec.finalizaEl.toDate()) : '',
          recMaxEnvios: rec?.maxEnvios ? String(rec.maxEnvios) : '10',
          ttlHoras: ([24, 48, 72, 168, 672].includes(data.ttlHoras)
            ? data.ttlHoras
            : 72) as TtlHoras,
          prioridad: data.prioridad ?? 'normal',
        });
        setHayCambios(false);
      } catch (err) {
        if (activo) {
          setNoEditableMotivo(
            err instanceof Error ? err.message : 'No se pudo cargar la campaña.',
          );
        }
      } finally {
        if (activo) setCargando(false);
      }
    })();
    return () => {
      activo = false;
    };
  }, [campanaIdParam]);

  // ─── Guardado (crear/actualizar + subida de imagen) ────────
  const persistir = useCallback(
    async (incluirProgramacion: boolean): Promise<string> => {
      const f = formRef.current;
      const idActual = campanaIdRef.current;

      if (!idActual) {
        // 1) Crear sin imagen si hay blob pendiente
        const imagenInicial = imagenSinBlob(f.imagen);
        const payload = buildPayload(f, imagenInicial, incluirProgramacion);
        const res = await crearCampanaFn(payload);
        const nuevoId = res.data.campanaId;
        setCampanaId(nuevoId);
        campanaIdRef.current = nuevoId;

        // 2) Subir blob y actualizar con la imagen
        if (f.imagen?.tipo === 'blob') {
          const subida = await subirImagenCampana(nuevoId, f.imagen.blob);
          await actualizarCampanaFn({
            campanaId: nuevoId,
            ...buildPayload(
              f,
              { imagenUrl: subida.url, imagenStoragePath: subida.path },
              incluirProgramacion,
            ),
          });
          setForm((prev) => ({
            ...prev,
            imagen: { tipo: 'existente', url: subida.url, storagePath: subida.path },
          }));
        }
        return nuevoId;
      }

      // Edición: subir imagen primero (si hay blob) y guardar con todo
      let imagenResuelta = imagenSinBlob(f.imagen);
      if (f.imagen?.tipo === 'blob') {
        const subida = await subirImagenCampana(idActual, f.imagen.blob);
        imagenResuelta = { imagenUrl: subida.url, imagenStoragePath: subida.path };
        setForm((prev) => ({
          ...prev,
          imagen: { tipo: 'existente', url: subida.url, storagePath: subida.path },
        }));
      }
      await actualizarCampanaFn({
        campanaId: idActual,
        ...buildPayload(f, imagenResuelta, incluirProgramacion),
      });
      return idActual;
    },
    [],
  );

  const guardar = useCallback(
    async (modo: ModoGuardado): Promise<string | null> => {
      const f = formRef.current;
      // Para borrador solo validamos contenido; programación se valida al programar.
      const todosErrores = validarForm(f);
      const erroresRelevantes: ErroresForm = { ...todosErrores };
      if (modo === 'borrador') {
        delete erroresRelevantes.programadaPara;
        delete erroresRelevantes.recHora;
        delete erroresRelevantes.recDiaMes;
        delete erroresRelevantes.recFinFecha;
        delete erroresRelevantes.recMaxEnvios;
      }
      setErrores(erroresRelevantes);
      if (Object.keys(erroresRelevantes).length > 0) return null;

      setGuardando(true);
      try {
        const id = await persistir(modo !== 'borrador');
        setUltimoGuardado(Date.now());
        setHayCambios(false);
        return id;
      } finally {
        setGuardando(false);
      }
    },
    [persistir],
  );

  const enviarAhora = useCallback(async () => {
    const id = await guardar('borrador');
    if (!id) return null;
    setEnviando(true);
    try {
      const res = await enviarCampanaFn({ campanaId: id });
      return { campanaId: id, resultado: res.data };
    } finally {
      setEnviando(false);
    }
  }, [guardar]);

  // ─── Autosave cada 30 s ────────────────────────────────────
  useEffect(() => {
    const intervalo = setInterval(() => {
      if (!campanaIdRef.current || !hayCambiosRef.current || guardandoRef.current) return;
      const f = formRef.current;
      // No autoguardar si el contenido mínimo es inválido (evita errores del backend)
      if (!f.nombre.trim() || !f.titulo.trim() || !f.descripcion.trim()) return;
      guardandoRef.current = true;
      persistir(true)
        .then(() => {
          setUltimoGuardado(Date.now());
          setHayCambios(false);
        })
        .catch((err) => {
          console.warn('[notificaciones] autosave falló:', err);
        })
        .finally(() => {
          guardandoRef.current = false;
        });
    }, 30_000);
    return () => clearInterval(intervalo);
  }, [persistir]);

  return {
    form,
    setField,
    errores,
    setErrores,
    cargando,
    campanaId,
    modoEdicion,
    estadoOriginal,
    noEditableMotivo,
    guardando,
    enviando,
    ultimoGuardado,
    hayCambios,
    guardar,
    enviarAhora,
  };
}
